import { stableStringify } from '@provision/formatting-utils';
import {
  ConversationMessageDraftSendAnd,
  ConversationMessageDraftSendAndAction,
  ConversationMessageDraftSendUnless,
} from 'src/graphql';

export type DraftScheduleSendAndAction = Omit<
  ConversationMessageDraftSendAndAction,
  '__typename'
>;

export type DraftScheduleSendAnd = Omit<ConversationMessageDraftSendAnd, '__typename'> & {
  actions: DraftScheduleSendAndAction[];
};

export type DraftScheduleSendUnless = Omit<ConversationMessageDraftSendUnless, '__typename'>;

export interface DraftSchedule {
  scheduledAt: string | null;
  sideEffects: DraftScheduleSendAnd | null;
  sendUnless: DraftScheduleSendUnless[] | null;
  discardedAt: string | null;
}

export const EMPTY_SCHEDULE: DraftSchedule = Object.freeze({
  scheduledAt: null,
  sideEffects: null,
  sendUnless: null,
  discardedAt: null,
});

export function createScheduleFromSource(
  source?: Partial<DraftSchedule> | null,
): DraftSchedule {
  if (!source) {
    return cloneSchedule();
  }

  return {
    scheduledAt: source.scheduledAt ?? null,
    sideEffects: cloneSendAnd(source.sideEffects),
    sendUnless: cloneSendUnless(source.sendUnless),
    discardedAt: source.discardedAt ?? null,
  };
}

export function cloneSchedule(schedule?: DraftSchedule | null): DraftSchedule {
  if (!schedule) {
    return { ...EMPTY_SCHEDULE };
  }

  return {
    scheduledAt: schedule.scheduledAt ?? null,
    sideEffects: cloneSendAnd(schedule.sideEffects),
    sendUnless: cloneSendUnless(schedule.sendUnless),
    discardedAt: schedule.discardedAt ?? null,
  };
}

export function cloneSendAnd(
  value?: ConversationMessageDraftSendAnd | DraftScheduleSendAnd | null,
): DraftScheduleSendAnd | null {
  if (!value?.actions?.length) {
    return null;
  }

  return {
    actions: value.actions
      .filter((action): action is DraftScheduleSendAndAction => Boolean(action?.type))
      .map((action) => ({
        type: action.type,
        value: action.value ?? null,
      })),
  };
}

export function cloneSendUnless(
  value?: (ConversationMessageDraftSendUnless | DraftScheduleSendUnless | null)[] | null,
): DraftScheduleSendUnless[] | null {
  if (!value?.length) {
    return null;
  }
  return value
    .filter((item): item is DraftScheduleSendUnless => Boolean(item?.type))
    .map((item) => ({
      type: item.type,
      value: item.value ?? null,
    }));
}

export function mergeSchedules(
  base: DraftSchedule,
  next: DraftSchedule | Partial<DraftSchedule>,
): DraftSchedule {
  const resolved = typeof next === 'object' && next ? next : {};

  return {
    scheduledAt:
      'scheduledAt' in resolved ? (resolved.scheduledAt ?? null) : (base.scheduledAt ?? null),
    sideEffects:
      'sideEffects' in resolved
        ? cloneSendAnd(resolved.sideEffects ?? null)
        : cloneSendAnd(base.sideEffects),
    sendUnless:
      'sendUnless' in resolved
        ? cloneSendUnless(resolved.sendUnless ?? null)
        : cloneSendUnless(base.sendUnless),
    discardedAt:
      'discardedAt' in resolved ? (resolved.discardedAt ?? null) : (base.discardedAt ?? null),
  };
}

export function areSchedulesEqual(a: DraftSchedule, b: DraftSchedule): boolean {
  return stableStringify(a) === stableStringify(b);
}

export function isScheduleEmpty(schedule: DraftSchedule | null | undefined): boolean {
  if (!schedule) {
    return true;
  }
  const hasSendAnd = Boolean(schedule.sideEffects?.actions?.length);
  const hasConditions = Boolean(schedule.sendUnless?.length);
  return !schedule.scheduledAt && !hasSendAnd && !hasConditions;
}
